import type { IOptionsComposableVueQueryOptions } from '@/types/shared/vue-query'
import type { IModel } from '@/types/rc/miners'
import { getPowerSupplyModels } from '@/services/core.service'
import { generateModelDisplayName, generateModelValueKey } from '@/util/rc'
import { useQuery } from '@tanstack/vue-query'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { ref } from 'vue'

/**
 * Composable for getPowerSupplyModels response, mapped to ElSelect options.
 *
 * @param refetchOnWindowFocus default: true; useQuery refetch on window focus.
 * @param refetchOnMount default: true; useQuery refetch on mount.
 * @returns getPowerSupplyModels useQuery state.
 */
export const usePSUModelOptions = (
  { refetchOnWindowFocus, refetchOnMount, enabled }: IOptionsComposableVueQueryOptions = {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: ref(true),
  }
) => {
  const { isFetching, isRefetching, isError, data, error, refetch } = useQuery({
    queryKey: ['power-supply-model-options'],
    queryFn: async () => {
      const psuModels = await getPowerSupplyModels()
      return psuModels.map((psuModel: IModel) => {
        return {
          label: generateModelDisplayName(psuModel.name!, psuModel.version),
          value: {
            ...psuModel,
            valueKey: generateModelValueKey(psuModel.name!, psuModel.version),
          },
        } as OptionType
      })
    },
    refetchOnWindowFocus,
    refetchOnMount,
    enabled,
  })

  return {
    isFetching,
    isRefetching,
    isError,
    data,
    error,
    refetch,
  }
}
